import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Badge,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { EditIcon } from "@/assets/icons";
import useMCPContext from "../useMCPContext";
import { RelativeComponentLoader } from "@/components/loader";
import MasksView from "@/pages/security/MasksView";
import { getIntegrationIcon } from "@/pages/teammate/utils";
import MemoryToggle from "@/pages/teammate/dataMates/memory/MemoryToggle";
import useIntegrations from "@/pages/teammate/useIntegrations";
import { useQuery } from "@tanstack/react-query";
import { getKnowledgeBaseSources } from "@/helpers/apis";
import KnowledgeCatalogCard from "@/pages/knowledgeCatalog/KnowledgeCatalogCard";
import { useTenantInfo } from "@/helpers/hooks";
import PrivacyBadge from "@/components/badges/PrivacyBadge";
import { Privacy } from "@/helpers/constants";

const Review = ({ updateStep }: { updateStep: (step: number) => void }) => {
  const { teammate } = useMCPContext();
  const { enableAdvancedGuardRails } = useTenantInfo();

  const {
    integrationsFromServer,
    customIntegrations,
    isIntegrationsFromServerLoading,
    isCustomIntegrationsLoading,
  } = useIntegrations();

  const { data: knowledgeSources, isLoading: isKnowledgeSourcesLoading } = useQuery({
    queryKey: ["knowledgeSources"],
    queryFn: () => getKnowledgeBaseSources(),
  });

  return (
    <Stack direction="column" className="al-w-full al-space-y-4">
      {/* Datamate Summary Section */}
      <Card>
        <CardHeader>
          <Stack align="center" justify="between">
            <Typography variant="section-heading">Datamate Summary</Typography>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateStep(1)}
              aria-label="Edit datamate summary"
            >
              <EditIcon />
            </Button>
          </Stack>
        </CardHeader>
        <CardContent>
          <Stack direction="column" className="al-space-y-2">
            <div>
              <Typography className="al-text-muted-foreground">Name</Typography>
              <Typography>{teammate?.name}</Typography>
            </div>
            <div>
              <Typography className="al-text-muted-foreground">Purpose</Typography>
              <Typography>{teammate?.description}</Typography>
            </div>
          </Stack>
        </CardContent>
      </Card>

      <Card className="al-mb-4">
        <CardContent className="al-pt-4">
          <MemoryToggle showInfo />
        </CardContent>
      </Card>

      {/* Integrations Section */}
      <Card>
        <CardHeader>
          <Stack align="center" justify="between">
            <Typography variant="section-heading">Integrations</Typography>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateStep(2)}
              aria-label="Edit integrations"
            >
              <EditIcon />
            </Button>
          </Stack>
        </CardHeader>
        <CardContent>
          {isIntegrationsFromServerLoading || isCustomIntegrationsLoading ? (
            <RelativeComponentLoader label="Loading integrations..." componentHeight={15} />
          ) : (
            <Stack direction="row" gap="lg" wrap={true}>
              {teammate?.integrations?.map(integration => {
                const integrationFromServer = integrationsFromServer?.find(
                  i => i.id === integration.id
                );
                const customIntegrationFromServer = customIntegrations?.items?.find(
                  i => i.id === integration.id
                );
                return (
                  <Stack
                    key={integration.id}
                    direction="row"
                    align="center"
                    className="al-space-x-1 al-bg-muted al-px-3 al-py-2 al-rounded-md al-w-[200px]"
                  >
                    {integrationFromServer?.id && getIntegrationIcon(integrationFromServer.id)}
                    {customIntegrationFromServer?.id &&
                      getIntegrationIcon(customIntegrationFromServer.id)}
                    <Typography>
                      {integrationFromServer?.name || customIntegrationFromServer?.name}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Guardrails Section */}
      <Card>
        <CardHeader>
          <Stack align="center" justify="between">
            <Typography variant="section-heading">Guardrails</Typography>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateStep(3)}
              aria-label="Edit guardrails"
            >
              <EditIcon />
            </Button>
          </Stack>
        </CardHeader>
        <CardContent>
          <Stack direction="column" className="al-space-y-4">
            {/* Data Protection */}
            <div>
              <Typography className="al-text-sm al-font-medium al-mb-2">Data Protection</Typography>
              <MasksView />
            </div>

            {/* Privacy - Only show if advanced guardrails are enabled */}
            {enableAdvancedGuardRails && (
              <div>
                <Typography variant="section-heading" className="al-mb-2">
                  Privacy
                </Typography>
                <div className="al-p-3 al-bg-gray-50 al-border al-border-gray-200 al-rounded-md">
                  <Stack direction="row" align="center" className="al-gap-2">
                    <PrivacyBadge privacy={teammate?.privacy || Privacy.PRIVATE} />
                    <Typography variant="body" className="al-text-sm">
                      {teammate?.privacy === Privacy.PUBLIC
                        ? "All members of your organization can access this datamate"
                        : "Only you can access this datamate"}
                    </Typography>
                  </Stack>
                </div>
              </div>
            )}

            {/* Knowledge Hub Summary - Only show if advanced guardrails are enabled */}
            {enableAdvancedGuardRails && (
              <div>
                <Typography className="al-text-sm al-font-medium al-mb-2">Knowledge Hub</Typography>
                {isKnowledgeSourcesLoading ? (
                  <RelativeComponentLoader
                    label="Loading knowledge sources..."
                    componentHeight={10}
                  />
                ) : (
                  <div>
                    {!teammate?.knowledge_bases || teammate.knowledge_bases.length === 0 ? (
                      <div className="al-p-3 al-bg-blue-50 al-border al-border-blue-200 al-rounded-md">
                        <Typography variant="body" className="al-text-blue-800 al-text-sm">
                          <strong>All Knowledge Hub Items</strong> - Access to all available
                          knowledge sources ({knowledgeSources?.length || 0} items).
                        </Typography>
                      </div>
                    ) : (
                      <div>
                        {(() => {
                          const validKnowledgeItems = teammate.knowledge_bases
                            .map(itemId => knowledgeSources?.find(item => item.id === itemId))
                            .filter((item): item is NonNullable<typeof item> => Boolean(item));

                          return (
                            <>
                              <Stack direction="row" align="center" className="al-gap-2 al-mb-3">
                                <Badge variant="secondary" className="al-text-xs">
                                  {validKnowledgeItems.length} of {knowledgeSources?.length || 0}{" "}
                                  selected
                                </Badge>
                              </Stack>
                              {/* Knowledge Hub Cards */}
                              <Stack direction="row" className="al-gap-4 !al-flex-wrap">
                                {validKnowledgeItems.map(knowledgeItem => (
                                  <KnowledgeCatalogCard
                                    key={knowledgeItem.id}
                                    item={knowledgeItem}
                                    showCheckbox={false}
                                    openInNewTab={true}
                                  />
                                ))}
                              </Stack>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default Review;
